/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Validators, FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material';

import {
  IcecontrolSettings,
  iceColumnDefinition,
  IcemattableSettings,
  IcepanelgroupSettings,
  IcepanelsectionSettings,
  IceexpansionpanelSettings,
  IcepanelcontainerRowSettings,
  IcepanelcontainerSettings,
  IcecontrolPermission,
  SECTION_CONTROL_ALIGNMENT,
  IcepanelSettings
} from 'projects/icemedx-lib/src/components/models/icesettings';

import { IceEntity } from 'projects/icemedx-lib/src/models/core/IceEntity';
import { provider } from 'projects/icemedx-lib/src/models/domain/provider';

import { AppService } from '../app.service';
import { IceNavService } from '../../../../icemedx-lib/src/components/ice-nav/ice-nav.service';
import { LoginService } from '../../../../icemedx-lib/src/login/login.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { VendorService } from '../../../../icemedx-lib/src/services/vendor/vendor.service';
import { ProviderService } from '../../../../icemedx-lib/src/services/provider/provider.service';
import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';

import { DomainProfile } from '../domainprofile';
import { MedicalcodesService } from '../../../../icemedx-lib/src/services/medicalcodes/medicalcodes.service';
import { payer } from '../../../../icemedx-lib/src/models/domain/payer';
import { PharmacyService } from '../../../../icemedx-lib/src/services/pharmacy/pharmacy.service';
import { PayerService } from '../../../../icemedx-lib/src/services/payer/payer.service';
import { basecode } from '../../../../icemedx-lib/src/models/ehr/subs/basecode';
import { MessageService } from '../../../../icemedx-lib/src/services/message/message.service';

@Component({
  selector: 'app-payer',
  templateUrl: './payer.component.html',
  styleUrls: ['./payer.component.scss'],
  providers: [AppService, IceNavService]
})
export class PayerComponent extends DomainProfile implements OnInit {
  // Data Members
  payerObj: payer = null;

  profileViewSettings: IcepanelcontainerSettings = null;
  btnSaveProfile: IcecontrolSettings = new IcecontrolSettings("SaveButton", { text: "Save Profile", class: [], glyphClass: "fa-plus-circle" }, null, null);

  constructor(appSvc: AppService,
    domainSvc: DomainService,
    vendorSvc: VendorService,
    proSvc: ProviderService,
    pharmSvc: PharmacyService, 
    payerSvc: PayerService, 
    medCodeSvc: MedicalcodesService,
    messageSvc: MessageService,
    loginSvc: LoginService,
    navSvc: IceNavService,
    router: Router) {
    super(appSvc, domainSvc, vendorSvc, proSvc, pharmSvc, payerSvc, medCodeSvc, messageSvc, loginSvc, navSvc, router);

    appSvc.defaultMenus();
    appSvc.addBreadcrumb("Payer Profile", {}, true, "");

    domainSvc.getPayer(appSvc.getCurrentAccount()).subscribe(
      pro => {
        this.profileViewSettings = this.viewSetup(pro);
      },
      error => {
      });
  }

  ngOnInit() {
    this.getAppService().refreshMenus();
  }

  viewSetup(dat: IceEntity): IcepanelcontainerSettings {
    let setup: IcepanelcontainerSettings = null;
    try {
      let feaAccess = this.getAppService().getPermission(userTypes.USER_FEATURES.ufSetup).default_access;
      this.btnSaveProfile.permissions = new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone ? userTypes.USER_FEATURE_ACCESS.ufaReadOnly : feaAccess);

      if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
        return null;
      }
      if (dat == null || dat["length"] == 0) {
        return null;
      }

      this.payerObj = <payer>dat[0];

      //////////////////////
      // Name
      if (this.payerObj.COUNTRIES == null || this.payerObj.COUNTRIES.length == 0) {
        this.payerObj.COUNTRIES = [new basecode("COUNTRY")];
      }
      if (this.payerObj.CURRENCIES == null || this.payerObj.CURRENCIES.length == 0) {
        this.payerObj.CURRENCIES = [new basecode("CURRENCY")];
      }

      let cntryCode: basecode = this.payerObj.COUNTRIES[0];
      let curcyCode: basecode = this.payerObj.CURRENCIES[0];
      let nameControls = super.viewName(this.payerObj.trans_account, this.payerObj.ENTS, this.payerObj.entity_key, false, "", cntryCode, curcyCode);
      let nameSections: IcepanelsectionSettings[] = [
        new IcepanelsectionSettings("NameViewSection", { class: ["yalla"], styles: { width: "100%" } }, null, nameControls, null)
      ];

      //////////////////////
      // Contact Info
      let contactTables = super.viewContact(this.payerObj.CONS);
      let contactSections = this.viewContactSection(contactTables, true);

      //////////////////////
      // Address Info
      let addressTables = this.viewAddress(this.payerObj.ADS);
      let addressSections = this.viewAddressSection(addressTables, true);

      //////////////////////
      // Identification Info
      let idCodeList = ['46', 'FI'];
      let idTables = super.viewIdentification(this.payerObj.IDS, idCodeList);
      let idSections = this.viewIdentificationSection(idTables, idCodeList, true);

      /////////////////////////
      // Network Connections
      let networkConnSections = this.viewNewOutboundNetworkConnections();
      let outboundNetworkConnectionTables = this.viewOutboundNetworkConnections(this.payerObj.CONNPARAMS, this.payerObj.TYPEREFS);
      let outboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, outboundNetworkConnectionTables)
      ];

      networkConnSections.push(new IcepanelsectionSettings("OutboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, outboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditOutboundNetworkConnections());

      let inboundNetworkConnectionTables = this.viewInboundNetworkConnections(this.payerObj.INCONNPARAMS, this.payerObj.TYPES);
      let inboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, inboundNetworkConnectionTables)
      ];
      networkConnSections.push(new IcepanelsectionSettings("InboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, inboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditInboundNetworkConnections());

      let namefrm: IcepanelSettings = new IceexpansionpanelSettings("NameView", "", "Name", "", nameSections, null, null, null)
        .enableForm();
      let nameFrmGrp = namefrm.getForm();
      let panels: IcepanelSettings[] = [
        namefrm,
        new IceexpansionpanelSettings("ContactView", "", "Contact Information", "", contactSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("AddressView", "", "Address Information", "", addressSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("IdentificationView", "", "Identification Information", "", idSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("NetworkConnectionView", "", "Network Connections", "", networkConnSections, null, null, null)
          .enableForm(nameFrmGrp),
      ];

      let panelrows: IcepanelcontainerRowSettings[] = [new IcepanelcontainerRowSettings(panels)];
      setup = new IcepanelcontainerSettings("ContactViewContainer", panelrows);
    }
    catch (e) {
      console.log(e);
    }
    return setup;
  }

  // Events
  onControlChange(event) {
    debugger;
    super.onControlChange(event);
  }

  onButtonClick(event) {
    debugger;
    this.onDefaultButtonClick(event, this.payerObj.CONNPARAMS, this.payerObj.TYPEREFS, this.editInboundRow);
  }

  public onRowClick(event) {
    debugger;
    super.onRowClick(event);
  }

  public onAddRowButtonClick(event) {
    this.onDefaultAddRowButtonClick(event,
      this.payerObj.CONS,
      this.payerObj.ADS,
      this.payerObj.IDS,
      this.payerObj.CONNPARAMS,
      this.payerObj.TYPEREFS,
      this.payerObj.INCONNPARAMS,
      this.payerObj.TYPES);
  }

  onSaveProfile(event) {
    if (false == this.updateName(this.payerObj.COUNTRIES, this.payerObj.CURRENCIES)) {
      // To do error log
      return;
    }

    this.updateContact(this.payerObj.CONS);
    this.updateAddress(this.payerObj.ADS);
    this.updateIdentification(this.payerObj.IDS);
    this.updateOutboundDomainConnection(this.payerObj.CONNPARAMS, this.payerObj.TYPEREFS);
    this.updateInboundDomainConnection(this.payerObj.INCONNPARAMS, this.payerObj.TYPES);
    this.getPayerService().updatePayer(this.payerObj).subscribe(
      data => {
        this.getRouter().navigate([this.getNavService().buildRoute("dashboard", false)]);
      },
      error => {
        console.log(error);
    });
  }
}
